import React, { useContext, useEffect, useState } from 'react'
import WizardButtons from '../WizardButtons'
import { includes, filter, keys, map, orderBy } from 'lodash'
import DataContext from '../../../data/context'
import { useTranslation } from 'react-i18next'
import WizardError from '../WizardError'
import analyticstracker from 'analyticstracker'
import { TAGMANAGER_TOOL_ARGS, TAGMANAGER_COMMERCE_ARGS, getToolStepInfo } from '../../../tagManager'

const Make = () => {
    const { inspection, data, setField, setNextVehicleDataStep, dealerConfig, fetchNextState, saveNavStep } =
        useContext(DataContext)
    const [errors, setErrors] = useState({})
    const { t } = useTranslation()
    const stepInfo = getToolStepInfo(dealerConfig.provider, 'brand')

    useEffect(() => {
        if (errors && keys(errors).length > 0) {
            validate()
        }
    }, [inspection])

    useEffect(() => {
        let aTrack = analyticstracker()
        aTrack.trackImpression('tool-start')
    }, [])

    const tagArgsStart = TAGMANAGER_TOOL_ARGS({
        event: 'start',
        ...stepInfo,
    })

    const tagArgsCommerce = TAGMANAGER_COMMERCE_ARGS({})

    const validate = () => {
        let errors = {}

        if (!inspection.make) {
            errors['make'] = t('Please select a brand')
            let aTrack = analyticstracker()
            let errorTrackingInfo = TAGMANAGER_TOOL_ARGS({
                event: 'error',
                ...stepInfo,
                errorType: 'user-error',
                errorMessage: errors.make,
            })
            let event = {
                event: 'tool-error',
                info: JSON.parse(errorTrackingInfo),
                commerce: JSON.parse(tagArgsCommerce),
            }
            aTrack.trackEvent(event)
        }

        setErrors(errors)

        return keys(errors).length <= 0
    }

    const validator = () => {
        return validate()
    }

    const handleMakeClick = (e, make) => {
        const aTrack = analyticstracker()
        aTrack.trackInteraction(e)

        const providerHandlers = {
            indicata: () => indicataMakeClickHandler(make),
            autralis: () => autralisMakeClickHandler(make),
        }

        providerHandlers[dealerConfig.provider](make)
    }

    const indicataMakeClickHandler = (make) => {
        saveNavStep({ link: make.link, rel: make.rel })
        fetchNextState(make.link)
    }

    const autralisMakeClickHandler = (make) => {
        setField('make', make.key)
        setNextVehicleDataStep()
    }

    //Autralis makes
    let allMakes = data && data.makes ? filter(data.makes, (m) => !m.disabled) : []
    let dealerMakes = orderBy(
        filter(dealerConfig.makes, (m) => m.takeOver),
        ['priority', 'name'],
        ['asc', 'asc'],
    )
    let otherMakes = orderBy(
        filter(
            allMakes,
            (m) =>
                !includes(
                    map(dealerConfig.makes, (m) => m.key),
                    m.key,
                ),
        ),
        ['name'],
        ['asc'],
    )

    function updateConfigMakesWithIndicataFields(configMakes, allMakes) {
        // Create a map for quick lookup of makes from allMakes
        const makeMap = new Map(allMakes.map((make) => [make.key, make]))
        console.log([...makeMap.keys()]);

        // Update configMakes with link and rel from allMakes if found
        configMakes = configMakes.map((make) => {
            if (makeMap.has(make.key)) {
                const foundMake = makeMap.get(make.key)
                return {
                    ...make,
                    link: foundMake.link || '',
                    rel: foundMake.rel || 'make',
                }
            }
            return make // Return as is if no match found
        })

        return configMakes
    }

    // Indicata makes
    if (dealerConfig.provider === 'indicata') {
        dealerMakes = orderBy(
            filter(updateConfigMakesWithIndicataFields(dealerConfig.makes, allMakes), (m) => m.takeOver),
            ['priority', 'name'],
            ['asc', 'asc'],
        )
        otherMakes = orderBy(
            filter(
                allMakes,
                (m) =>
                    !includes(
                        map(dealerConfig.makes, (m) => m.key),
                        m.key,
                    ),
            ),
            ['name'],
            ['asc'],
        )
    }

    return (
        <div
            data-tracking-event="tool-start"
            data-tracking-info={tagArgsStart}
            data-tracking-commerce={tagArgsCommerce}
        >
            <div className="text-color_one text-24 font-bold mb-4 mt-10 text-center">{t('Make')}</div>

            <div className="text-16 leading-6 mb-4 mt-6">{t('Select the brand of your vehicle')}:</div>

            {dealerMakes && dealerMakes.length > 0 && (
                <DealerMakes dealerMakes={dealerMakes} inspection={inspection} handleMakeClick={handleMakeClick} />
            )}

            <div className="flex flex-wrap mb-10">
                {
                    <OtherMakes
                        otherMakes={otherMakes}
                        inspection={inspection}
                        tagArgsCommerce={tagArgsCommerce}
                        handleMakeClick={handleMakeClick}
                    />
                }
            </div>

            <WizardError errors={errors} />

            <WizardButtons validatorFunction={validator} />
        </div>
    )
}

const DealerMakes = ({ dealerMakes, inspection, handleMakeClick }) => {
    const { dealerConfig } = useContext(DataContext)
    const stepInfo = getToolStepInfo(dealerConfig.provider, 'brand')
    return (
        <React.Fragment>
            <div className="flex flex-wrap mb-3">
                {map(dealerMakes, (make, i) => {
                    const selected = make.key === inspection.make
                    const tagArgsSubmit = TAGMANAGER_TOOL_ARGS({
                        event: 'submit',
                        ...stepInfo,
                        toolStepOption: make.name,
                    })
                    const tagArgsCommerce = TAGMANAGER_COMMERCE_ARGS({})
                    return (
                        <div key={`make_${make.key}`} className={`w-1/2 mb-2 ${i % 2 !== 1 ? 'pr-2' : ''}`}>
                            <div
                                className={`flex items-center flex-col justify-center bg-color_five border text-14 rounded-lg cursor-pointer ${
                                    selected ? 'border-color_one text-color_one' : 'border-color_five text-color_three'
                                } hover:border-color_one hover:text-color_one capitalize p-2`}
                                // style={{height: 70}}
                                data-tracking-event="tool-submit"
                                data-tracking-info={tagArgsSubmit}
                                data-tracking-commerce={tagArgsCommerce}
                                onClick={(e) => handleMakeClick(e, make)}
                            >
                                <i
                                    data-name={make.key.replace(' ', '-')}
                                    className={`car-logo icon-${make.key.replace(' ', '-')} ${
                                        selected ? 'text-color_one' : ''
                                    }`}
                                    style={{ fontSize: 50 }}
                                />
                                <span className="mt-2 font-bold w-full truncate text-center">{make.name}</span>
                                {/*<span className={make.key}>{make.name}</span>*/}
                            </div>
                        </div>
                    )
                })}
            </div>
            <hr className="solid mb-5 border-color_five" />
        </React.Fragment>
    )
}

const OtherMakes = ({ otherMakes, inspection, tagArgsCommerce, handleMakeClick }) => {
    const { dealerConfig } = useContext(DataContext)
    const stepInfo = getToolStepInfo(dealerConfig.provider, 'brand')
    return map(otherMakes, (make, i) => {
        const selected = make.key === inspection.make
        const tagArgsSubmit = TAGMANAGER_TOOL_ARGS({
            event: 'submit',
            ...stepInfo,
            toolStepOption: make.name,
        })
        return (
            <div key={`make_${make.key}`} className={`w-1/3 mb-2 ${i % 3 !== 2 ? 'pr-2' : ''}`}>
                <div
                    className={`flex items-center text-center justify-center bg-color_five border text-14 rounded-lg cursor-pointer ${
                        selected ? 'border-color_one text-color_one' : 'border-color_five text-color_three'
                    } hover:border-color_one hover:text-color_one capitalize`}
                    style={{ height: 70 }}
                    data-tracking-event="tool-submit"
                    data-tracking-info={tagArgsSubmit}
                    data-tracking-commerce={tagArgsCommerce}
                    onClick={(e) => handleMakeClick(e, make)}
                >
                    {make.name}
                </div>
            </div>
        )
    })
}

export default Make
